import React from "react";
import {
  Box,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useColorModeValue,
} from "@chakra-ui/react";
import { LegalPageShell } from "./legal/LegalPageShell";
import PageSeo from "../../seo/PageSeo";
import { DEFAULT_KEYWORDS, SITE_URL } from "../../seo/siteConfig";
import { faqPageSchema, breadcrumbSchema } from "../../seo/structuredData";

export const FAQ_ITEMS = [
    {
      question: "What is Manage360?",
      answer:
        "One website after you sign in: notes, files, a simple money tracker, project boards, todos, repeating chores, calendar tools, and shared group expenses.",
    },
    {
      question: "Is it free?",
      answer:
        "The main dashboard is free. AI features on some screens use prepaid AI Usage Credits (rupees via Razorpay) if you choose to buy them.",
    },
    {
      question: "Where does voice work?",
      answer:
        "After login, on Portfolio AI, Project AI, Todos AI, Daily Chore AI, Screen AI, and Media AI—only while you have AI Usage Credits.",
    },
    {
      question: "How do reminders work?",
      answer:
        "You set them in the app for chores and calendar. Delivery uses the email or phone you saved; it depends on carriers and your setup.",
    },
    {
      question: "What is the portfolio tab—not a bank, right?",
      answer:
        "Correct. Portfolio AI is a personal worksheet for income and expenses you enter. It is not a regulated bank account or investment advisor.",
    },
    {
      question: "How do projects and todos differ?",
      answer:
        "Project AI uses board columns and due dates for bigger streams of work. Todos AI are nested checklists for day-to-day items. Many people use both.",
    },
    {
      question: "How do group bills (Splitwise-style) work?",
      answer:
        "You record who paid whom in a group and see balances. Settling happens outside the app; Manage360 tracks what is owed.",
    },
    {
      question: "Is my data secure? How do I delete or export?",
      answer:
        "We follow normal security practices; see the Privacy Policy for details. To delete or export, contact us from the Contact page with the email or phone on your account.",
    },
    {
      question: "Who built Manage360?",
      answer:
        "Our Software Solutions Pvt. Ltd. Legal and privacy contacts are listed on those policy pages.",
    },
];

const FAQs = () => {
  const faqs = FAQ_ITEMS;

  const itemBorder = useColorModeValue("gray.100", "gray.600");
  const itemBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const btnHover = useColorModeValue("teal.50", "whiteAlpha.100");
  const expandedBg = useColorModeValue("teal.50", "whiteAlpha.100");
  const qColor = useColorModeValue("gray.800", "white");
  const aColor = useColorModeValue("gray.600", "gray.300");
  const itemShadow = useColorModeValue(
    "0 10px 32px -20px rgba(15,118,110,0.18)",
    "0 14px 36px -20px rgba(0,0,0,0.42)"
  );
  const itemHoverShadow = useColorModeValue(
    "0 20px 44px -24px rgba(45,212,191,0.35)",
    "0 22px 48px -22px rgba(45,212,191,0.15)"
  );

  const jsonLd = [
    faqPageSchema(faqs),
    breadcrumbSchema([
      { name: "Home", url: SITE_URL },
      { name: "FAQs", url: `${SITE_URL}/faqs` },
    ]),
  ];

  return (
    <>
      <PageSeo
        title="FAQs — Screen AI, Media AI, credits, security, group bills"
        description="Answers about Manage360: free dashboard, AI Usage Credits, Screen AI and Media AI, portfolio vs bank, projects vs todos, Splitwise-style groups, and data privacy."
        keywords={`${DEFAULT_KEYWORDS}, Manage360 FAQ, AI credits FAQ`}
        canonicalPath="/faqs"
        jsonLd={jsonLd}
      />
    <LegalPageShell
      badge="Help"
      title="FAQs"
      intro="Common questions—email us anytime if yours isn’t listed."
      maxW="min(100%, 800px)"
    >
      <Accordion allowMultiple reduceMotion>
        {faqs.map((faq, index) => (
          <AccordionItem
            key={faq.question}
            border="1px solid"
            borderColor={itemBorder}
            borderRadius="xl"
            mb={3}
            overflow="hidden"
            bg={itemBg}
            boxShadow={itemShadow}
            transition="transform 0.22s ease, box-shadow 0.22s ease"
            _hover={{
              transform: "translateY(-3px)",
              boxShadow: itemHoverShadow,
            }}
          >
            <h2>
              <AccordionButton
                py={5}
                px={5}
                _expanded={{ bg: expandedBg }}
                _hover={{ bg: btnHover }}
                transition="background 0.2s ease"
              >
                <Box flex="1" textAlign="left">
                  <Text fontWeight="semibold" fontSize="md" color={qColor} pr={2}>
                    {index + 1}. {faq.question}
                  </Text>
                </Box>
                <AccordionIcon color="teal.500" />
              </AccordionButton>
            </h2>
            <AccordionPanel px={5} pb={5} pt={0}>
              <Text fontSize="md" color={aColor} lineHeight="tall">
                {faq.answer}
              </Text>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </LegalPageShell>
    </>
  );
};

export default FAQs;
